import moment from 'moment';
import { PriceInfo } from '../../components/price/components/Price';
import { Accommodation, Property } from '../../routes/reducers/types';
import {
  TrackGenerateLeadEvent,
  TrackAddToCartEvent,
  TrackViewItem,
  TrackViewItemEvent,
} from './types';

const defaultCurrency = 'EUR';
const defaultAccommodationType = 'holiday_home';
const generateLeadDefaultValue = 400;

function buildTrackingItems(accommodation?: Accommodation): TrackViewItem[] | null {
  if (!accommodation) {
    return null;
  }
  return [
    {
      item_id: accommodation.id.toString(),
      item_name: accommodation.display_title,
      item_list_name: 'accommodation_page',
      item_category: 'rent',
    },
  ];
}

function accommodationType(
  accommodation?: Pick<Accommodation, 'kind'>,
  property?: Pick<Property, 'suite'>,
): string {
  if (property?.suite) {
    return 'suite';
  }
  return accommodation?.kind || defaultAccommodationType;
}

function bookingWindow(priceInfo: PriceInfo): number {
  return moment().diff(moment(priceInfo.arrival), 'days');
}

function bookingDuration(priceInfo: PriceInfo): number {
  return moment(priceInfo.arrival).diff(moment(priceInfo.departure), 'days');
}

export function createViewItemEvent(
  accommodation?: Accommodation,
  property?: Pick<Property, 'locality'>,
): TrackViewItemEvent {
  return {
    event: 'view_item',
    currency: defaultCurrency,
    value: 0,
    location: property?.locality || '',
    type: accommodationType(accommodation),
    items: buildTrackingItems(accommodation),
  };
}

export function createAddToCartEvent(
  accommodation: Pick<Accommodation, 'id' | 'display_title' | 'kind'>,
  property: Pick<Property, 'locality' | 'suite'>,
  price: PriceInfo | null,
): TrackAddToCartEvent {
  return {
    event: 'add_to_cart',
    currency: defaultCurrency,
    value: price?.price ?? 0,
    location: property?.locality || '',
    type: accommodationType(accommodation, property),
    booking_window: price != null ? bookingWindow(price) : undefined,
    booking_duration: price != null ? bookingDuration(price) : undefined,
    items: [
      {
        item_id: accommodation.id.toString(),
        item_name: accommodation.display_title,
        price: price?.price ?? 0,
        quantity: 1,
        item_category: 'rent',
      },
    ],
  };
}

export function createGenerateLeadEvent(
  accommodation: Accommodation,
  property: Property,
): TrackGenerateLeadEvent {
  return {
    event: 'generate_lead',
    parameters: {
      currency: defaultCurrency,
      value: generateLeadDefaultValue,
      booking_window: 0,
      booking_duration: 0,
      location: property?.locality || '',
      type: accommodationType(accommodation, property),
      lead_item_id: accommodation.id.toString(),
      lead_item_name: accommodation.display_title,
    },
  };
}
